// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminLogs.js
// Admin: the jobs' log and report files (/opt/woody-jobs/eodhd/logs and
// /opt/woody-jobs/log), newest first, with a filter and a download button each -
// for attaching reports (e.g. test_prompts.py's prompt_test_*.md) elsewhere.
// API: admin.py /api/admin/logs and /logs/download (@admin_required).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px 20px' },
  input:   { width: '100%', boxSizing: 'border-box', padding: '8px 12px', marginBottom: '12px', fontSize: '13px',
             border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)' },
  row:     { display: 'flex', alignItems: 'center', gap: '12px', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' },
  name:    { flex: 1, fontFamily: "'DM Mono', monospace", wordBreak: 'break-all' },
  meta:    { color: 'var(--text-muted)', fontSize: '12px', whiteSpace: 'nowrap' },
  btn:     { padding: '4px 12px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px',
             fontWeight: 500, background: 'var(--accent-dim)', color: 'var(--accent)' },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
};

const size = b => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`;
const when = t => new Date(t * 1000).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function AdminLogs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(() => {
    api.get('/admin/logs').then(r => setFiles(r.data)).catch(() => setErr('Could not load the file list'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = f => {
    api.get(`/admin/logs/download?dir=${encodeURIComponent(f.dir)}&name=${encodeURIComponent(f.name)}`,
            { responseType: 'blob' })
      .then(r => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = f.name;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => setErr(`Could not download ${f.name}`));
  };

  const shown = (files || []).filter(f => f.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <div style={s.heading}>Logs &amp; Reports</div>
        <div style={s.sub}>Job logs and reports from the server, newest first. Download to attach them elsewhere.</div>
        <div style={s.section}>
          <input style={s.input} placeholder="Filter by name, e.g. prompt_test" value={q} onChange={e => setQ(e.target.value)} />
          {files === null && !err && <div style={s.meta}>Loading…</div>}
          {shown.map(f => (
            <div key={`${f.dir}/${f.name}`} style={s.row}>
              <span style={s.name}>{f.name}</span>
              <span style={s.meta}>{f.dir}</span>
              <span style={s.meta}>{size(f.size)}</span>
              <span style={s.meta}>{when(f.mtime)}</span>
              <button style={s.btn} onClick={() => download(f)}>Download</button>
            </div>
          ))}
          {files && !shown.length && <div style={s.meta}>No matching files</div>}
          {err && <div style={s.err}>{err}</div>}
        </div>
      </div>
    </div>
  );
}
